// src/utils/purchaseOrderUtils.js

// 🛠️ Purchase Order Utility Functions
export class PurchaseOrderUtils {
  
  // Generate temporary ID for new items
  static generateTempId() {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  // Format currency amount
  static formatCurrency(amount) {
    if (!amount && amount !== 0) return 'Rs. 0.00'
    
    const numericAmount = Number(amount) || 0
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount).replace('LKR', 'Rs.')
  }
  
  // Format number with commas
  static formatNumber(number) {
    if (!number && number !== 0) return '0'
    return Number(number).toLocaleString()
  }
  
  // Format date for display
  static formatDate(dateString, format = 'short') {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      const options = {
        short: { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        },
        long: { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          weekday: 'long'
        },
        time: { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
      }
      
      return date.toLocaleDateString('en-US', options[format] || options.short)
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }
  
  // Calculate item total (quantity × unit price)
  static calculateItemTotal(item) {
    if (!item || !item.quantity || !item.unitPrice) return 0
    return Number(item.quantity) * Number(item.unitPrice)
  }
  
  // Calculate order total from all items
  static calculateOrderTotal(orderItems) {
    if (!Array.isArray(orderItems)) return 0
    
    return orderItems.reduce((total, item) => {
      return total + this.calculateItemTotal(item)
    }, 0)
  }
  
  // Get status configuration with colors and icons
  static getStatusConfig(status) {
    // Normalize status to lowercase for consistent matching
    const normalizedStatus = status?.toLowerCase()
    
    const statusMap = {
      'pending': {
        label: 'Pending',
        icon: '⏳',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      'approved': {
        label: 'Approved',
        icon: '✅',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'completed': {
        label: 'Completed',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'complete': { // Handle "complete" without 'd'
        label: 'Completed',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'cancelled': {
        label: 'Cancelled',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      'canceled': { // Handle American spelling
        label: 'Cancelled',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      }
    }
    
    return statusMap[normalizedStatus] || {
      label: status || 'Unknown',
      icon: '❓',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    }
  }
  
  // Filter items based on search term and fields
  static filterItems(items, searchTerm, searchFields = []) {
    if (!searchTerm || !Array.isArray(items)) return items
    
    const lowercaseTerm = searchTerm.toLowerCase()
    
    return items.filter(item => {
      return searchFields.some(field => {
        // Handle nested field paths (e.g., 'supplier.name')
        const value = field.split('.').reduce((obj, key) => obj?.[key], item)
        return value && value.toString().toLowerCase().includes(lowercaseTerm)
      })
    })
  }
  
  // Sort items by a specific field
  static sortItems(items, sortField, sortOrder = 'asc') {
    if (!Array.isArray(items)) return items
    
    return [...items].sort((a, b) => {
      // Handle nested field paths
      const valueA = sortField.split('.').reduce((obj, key) => obj?.[key], a) || ''
      const valueB = sortField.split('.').reduce((obj, key) => obj?.[key], b) || ''
      
      // Convert to strings for comparison
      const strA = valueA.toString().toLowerCase()
      const strB = valueB.toString().toLowerCase()
      
      if (sortOrder === 'asc') {
        return strA.localeCompare(strB)
      } else {
        return strB.localeCompare(strA)
      }
    })
  }
  
  // Export data to CSV
  static exportToCSV(data, filename, columns = []) {
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('No data to export')
      return
    }
    
    try {
      // Use provided columns or extract from data keys
      const headers = columns.length > 0 
        ? columns.map(col => col.header || col.key)
        : Object.keys(data[0])
      
      const csvContent = [
        // Header row
        headers.join(','),
        // Data rows
        ...data.map(row => {
          const values = columns.length > 0
            ? columns.map(col => {
                const value = row[col.key] || ''
                // Escape commas and quotes in values
                return `"${value.toString().replace(/"/g, '""')}"`
              })
            : Object.values(row).map(value => `"${value?.toString().replace(/"/g, '""') || ''}"`)
          
          return values.join(',')
        })
      ].join('\n')
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up the URL object
        setTimeout(() => URL.revokeObjectURL(url), 100)
      }
    } catch (error) {
      console.error('Error exporting to CSV:', error)
      throw new Error('Failed to export data to CSV')
    }
  }
  
  // Validate purchase order data
  static validatePurchaseOrder(orderData) {
    const errors = {}
    
    // Required field validations
    if (!orderData.requestingBranch?.trim()) {
      errors.requestingBranch = 'Requesting branch is required'
    }
    
    if (!orderData.supplierType) {
      errors.supplierType = 'Supplier type is required'
    }
    
    if (orderData.supplierType === 'supplier' && !orderData.supplier?.trim()) {
      errors.supplier = 'Supplier is required'
    }
    
    if (orderData.supplierType === 'warehouse' && !orderData.warehouse?.trim()) {
      errors.warehouse = 'Warehouse is required'
    }
    
    if (!orderData.orderDate) {
      errors.orderDate = 'Order date is required'
    }
    
    if (!orderData.expectedDeliveryDate) {
      errors.expectedDeliveryDate = 'Expected delivery date is required'
    }
    
    // Date validation
    if (orderData.orderDate && orderData.expectedDeliveryDate) {
      const orderDate = new Date(orderData.orderDate)
      const deliveryDate = new Date(orderData.expectedDeliveryDate)
      
      if (deliveryDate <= orderDate) {
        errors.expectedDeliveryDate = 'Expected delivery date must be after order date'
      }
    }
    
    // Order items validation
    if (!orderData.orderItems || orderData.orderItems.length === 0) {
      errors.orderItems = 'At least one item is required'
    } else {
      // Validate each item
      orderData.orderItems.forEach((item, index) => {
        if (!item.quantity || item.quantity <= 0) {
          errors[`item_${index}_quantity`] = `Item ${index + 1}: Quantity must be greater than 0`
        }
        
        if (!item.productId && !item.rawMaterialId) {
          errors[`item_${index}_selection`] = `Item ${index + 1}: Please select a product or raw material`
        }
      })
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Generate PO number (fallback for client-side)
  static generatePONumber() {
    const year = new Date().getFullYear()
    const month = String(new Date().getMonth() + 1).padStart(2, '0')
    const timestamp = Date.now().toString().slice(-6)
    return `PO-${year}${month}-${timestamp}`
  }
  
  // Check if user can edit purchase order
  static canEdit(purchaseOrder, userRole = null) {
    if (!purchaseOrder) return false
    
    // Only pending orders can be edited
    if (purchaseOrder.status !== 'pending') return false
    
    // Additional role-based checks can be added here
    return true
  }
  
  // Check if user can delete purchase order
  static canDelete(purchaseOrder, userRole = null) {
    if (!purchaseOrder) return false
    
    // Only pending orders can be deleted
    if (purchaseOrder.status !== 'pending') return false
    
    // Additional role-based checks can be added here
    return true
  }
  
  // Get supplier or warehouse name from order
  static getSupplierName(purchaseOrder) {
    if (!purchaseOrder) return 'N/A'
    
    if (purchaseOrder.supplierType === 'supplier') {
      return purchaseOrder.supplier?.name || 'Unknown Supplier'
    } else {
      return purchaseOrder.warehouse?.name || 'Unknown Warehouse'
    }
  }
  
  // Get order summary for display
  static getOrderSummary(purchaseOrder) {
    if (!purchaseOrder) return null
    
    const totalItems = purchaseOrder.orderItems?.length || 0
    const totalQuantity = purchaseOrder.orderItems?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
    const totalAmount = this.calculateOrderTotal(
      purchaseOrder.orderItems?.map(item => ({
        quantity: item.quantity,
        unitPrice: item.productSnapshot?.cost || item.rawMaterialSnapshot?.cost || 0
      })) || []
    )
    
    return {
      totalItems,
      totalQuantity,
      totalAmount,
      formattedAmount: this.formatCurrency(totalAmount)
    }
  }
  
  // Debounce function for search
  static debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}

export default PurchaseOrderUtils